import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Shuffle } from "lucide-react";

const WORDS = ["hasard", "sort", "clic", "pif"];

// Composant pour les mots qui défilent - version CSS pure
const RotatingWords = ({ size = "default", variant = "default" }) => {
  const [index, setIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const textSizes = {
    small: "text-sm",
    default: "text-lg",
    large: "text-xl",
    footer: "text-xs"
  };

  const textColors = {
    default: "text-primary",
    light: "text-white/80",
    muted: "text-white/60"
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true);
      setTimeout(() => {
        setIndex(prev => (prev + 1) % WORDS.length);
        setIsAnimating(false);
      }, 300);
    }, 2500);
    
    return () => clearInterval(interval);
  }, []);

  return (
    <span className={`relative inline-flex items-baseline ml-1 overflow-hidden ${
      size === "footer" ? "w-[50px] h-[1em]" : "w-[90px] h-[1em]"
    }`}>
      <span
        className={`absolute left-0 bottom-0 font-normal transition-all duration-300 ${textSizes[size]} ${textColors[variant]} ${
          isAnimating ? "opacity-0 -translate-y-4" : "opacity-100 translate-y-0"
        }`}
      >
        {WORDS[index]}
      </span>
    </span>
  );
};

// Logo animé réutilisable
export const AnimatedLogo = ({ linkTo = "/", showAnimation = true, size = "default", variant = "default", className = "" }) => {
  const iconSizes = {
    small: "w-8 h-8",
    default: "w-10 h-10",
    large: "w-12 h-12",
    footer: "w-5 h-5"
  };
  
  const textSizes = {
    small: "text-xl",
    default: "text-2xl",
    large: "text-3xl",
    footer: "text-sm"
  };
  
  const iconInnerSizes = {
    small: "w-4 h-4",
    default: "w-5 h-5",
    large: "w-6 h-6",
    footer: "w-3 h-3"
  };

  const textColors = {
    default: "text-foreground",
    light: "text-white",
    muted: "text-white/60"
  };

  const bgColors = {
    default: "bg-primary",
    light: "bg-white/20",
    muted: "bg-white/10"
  };

  const LogoContent = () => (
    <>
      <div className={`${iconSizes[size]} rounded-xl ${bgColors[variant]} flex items-center justify-center`}>
        <Shuffle className={`${iconInnerSizes[size]} text-white`} />
      </div>
      <span className={`${textSizes[size]} font-bold ${textColors[variant]} flex items-baseline`} style={{ fontFamily: 'Outfit' }}>
        TIREO{showAnimation && <RotatingWords size={size} variant={variant} />}
      </span>
    </>
  );

  if (linkTo) {
    return (
      <Link to={linkTo} className={`flex items-center gap-2 ${className}`}>
        <LogoContent />
      </Link>
    );
  }

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      <LogoContent />
    </div>
  );
};

// Footer avec logo animé
export const AnimatedFooter = ({ organizerName, variant = "muted" }) => {
  return (
    <div className="text-center mt-8">
      {organizerName && (
        <p className="text-white/40 text-sm mb-2">Organisé par {organizerName}</p>
      )}
      <div className="flex items-center justify-center gap-2 text-white/50">
        <span className="text-sm">Propulsé par</span>
        <AnimatedLogo linkTo={null} showAnimation={true} size="footer" variant={variant} />
      </div>
    </div>
  );
};

export default AnimatedLogo;
